import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, ArrowRight, MapPin, Calendar, FileSpreadsheet, Trash2, Pencil, MapPinned, Crosshair, Users, EyeOff, Eye, Lock, Search, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel, api } from '../lib/api';
import { inrCompact, formatDate, statusColor } from '../lib/format';
import { toast } from 'sonner';
import BillingPaymentTermsFields, { normalizePaymentTerms } from '../components/BillingPaymentTermsFields';
import { useMajorDelete } from '../components/MajorDeleteDialog';
import ProjectTeamPicker from '../components/ProjectTeamPicker';
import DriveAttachments from '../components/DriveAttachments';
import LocationCombobox from '../components/LocationCombobox';
import { SiteDeliveryFields, validateSiteFields } from '../components/projects/SiteDeliveryFields';
import ClientCombobox from '../components/ClientCombobox';
import EmployeePicker from '../components/EmployeePicker';
import { useFieldConfig } from '../hooks/useFieldConfig';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_FORM = {
  status: 'Active',
  risk_level: 'Medium',
  contract_type: 'Lumpsum Contract',
  billing_type: 'Milestone Basis',
  ra_bill_frequency: 'Monthly',
  payment_terms: {},
  geofence: { lat: '', lng: '', radius_m: 500 },
  maps_link: '',
  team: [],
};

export default function Projects() {
  const { user } = useAuth() || {};
  const [requestDelete, MajorDeleteHost] = useMajorDelete();
  const fc = useFieldConfig('project_create');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [resolvingLoc, setResolvingLoc] = useState(false);
  const [boqFile, setBoqFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState({});
  const [billingTypes, setBillingTypes] = useState(['Milestone Basis', 'RA Bill Basis']);
  const [raFrequencies, setRaFrequencies] = useState(['Weekly', 'Fortnightly', 'Monthly']);
  const [defaultPT, setDefaultPT] = useState({});
  // Inactive projects gated to exec roles (MD/CEO/CMD/CFO/super_admin).
  // Non-exec users see only Active projects; exec users get a toggle to surface
  // Hold/Completed/Cancelled/Archived.
  const [showInactive, setShowInactive] = useState(false);
  const [view, setView] = useState('cards'); // 'cards' | 'list'
  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isExec = role === 'super_admin' || role === 'md' || role === 'ceo' || role === 'cmd' || role === 'cfo' || ['MD', 'CEO', 'CMD', 'CFO'].includes(dept);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      // Project Management page intentionally shows ALL projects (including
      // On Hold / Completed / Cancelled) so users can browse history,
      // archive, or reactivate them. Every OTHER page in the app should
      // call `/projects` without this flag → it will return only Active.
      setProjects(await apiGet('/projects', { include_inactive: true }));
    } catch {}
    try {
      const t = await apiGet('/projects/stage-templates');
      setTemplates(t.templates || {});
      if (t.billing_types) setBillingTypes(t.billing_types);
      if (t.ra_bill_frequencies) setRaFrequencies(t.ra_bill_frequencies);
      if (t.default_payment_terms) {
        setDefaultPT(t.default_payment_terms);
        setForm(f => ({ ...f, payment_terms: { ...t.default_payment_terms, ...(f.payment_terms || {}) } }));
      }
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    await requestDelete({
      entity_type: 'project',
      entity_id: p.id,
      entity_name: `${p.code || ''} · ${p.name}`.trim(),
      contract_value: p.contract_value,
      directDelete: () => apiDel(`/projects/${p.id}`),
      onSuccess: load,
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setBoqFile(null);
    setForm({ ...EMPTY_FORM, payment_terms: { ...defaultPT } });
    setFormErrors({});
    setOpen(true);
  };

  const openEdit = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(p.id);
    setBoqFile(null);
    const geo = p.geofence || {};
    setForm({
      ...EMPTY_FORM,
      ...p,
      payment_terms: { ...defaultPT, ...(p.payment_terms || {}) },
      geofence: {
        lat: geo.lat ?? '',
        lng: geo.lng ?? '',
        radius_m: geo.radius_m ?? 500,
      },
      team: Array.isArray(p.team) ? p.team : [],
      maps_link: '',
    });
    setOpen(true);
  };

  const resolveMapsLink = async () => {
    const url = (form.maps_link || '').trim();
    if (!url) { toast.error('Paste a Google Maps link or "lat,lng" first'); return; }
    setResolvingLoc(true);
    try {
      const r = await apiPost('/settings/resolve-location', { url });
      setForm(f => ({ ...f, geofence: { ...f.geofence, lat: r.lat, lng: r.lng } }));
      toast.success(`Coordinates: ${r.lat.toFixed(5)}, ${r.lng.toFixed(5)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not extract coordinates from this link';
      toast.error(detail, { duration: 9000 });
    } finally { setResolvingLoc(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }
    setResolvingLoc(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setForm(f => ({
          ...f,
          geofence: { ...f.geofence, lat: Number(latitude.toFixed(7)), lng: Number(longitude.toFixed(7)) },
        }));
        toast.success(
          `Current location: ${latitude.toFixed(5)}, ${longitude.toFixed(5)} (±${Math.round(accuracy)}m)`,
          { duration: 5000 }
        );
        setResolvingLoc(false);
      },
      (err) => {
        const map = {
          1: 'Permission denied. Allow location access in your browser and try again.',
          2: 'Position unavailable — GPS signal weak. Try moving to an open area.',
          3: 'Location request timed out. Try again.',
        };
        toast.error(map[err.code] || `Could not get current location: ${err.message}`, { duration: 7000 });
        setResolvingLoc(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    // App-level validation — non-negative money + end-date not before start-date.
    const errs = {};
    if (form.contract_value !== '' && form.contract_value != null && Number(form.contract_value) < 0)
      errs.contract_value = 'Contract value cannot be negative';
    if (form.start_date && form.end_date && form.end_date < form.start_date)
      errs.end_date = 'End date cannot be before the start date';
    Object.assign(errs, validateSiteFields(form));
    if (Object.keys(errs).length) { setFormErrors(errs); toast.error(Object.values(errs)[0]); return; }
    setFormErrors({});
    setSubmitting(true);
    try {
      // Build geofence sub-object only if both lat and lng are present
      const geo = form.geofence || {};
      const geofence = (geo.lat !== '' && geo.lng !== '' && !Number.isNaN(Number(geo.lat)) && !Number.isNaN(Number(geo.lng)))
        ? { lat: Number(geo.lat), lng: Number(geo.lng), radius_m: Number(geo.radius_m || 500) }
        : null;
      const { maps_link, wo_code, ...rest } = form;  // strip helper-only fields
      const payload = {
        ...rest,
        contract_value: Number(form.contract_value || 0),
        payment_terms: normalizePaymentTerms(form.payment_terms, defaultPT),
        geofence,
        team: Array.isArray(form.team) ? form.team : [],
      };
      if (!editingId) {
        payload.spent = 0; payload.workforce = 0;
        // progress deliberately omitted — backend defaults it to null ("—") until
        // BOQ/WBS data gives compute_project_progress() something to roll up.
        // Auto-fill first WO code if blank — keeps backend in sync with UI hint
        const finalWo = (wo_code || (form.code ? `${form.code.toUpperCase()}.1` : '')).trim();
        if (finalWo) payload.first_wo_code = finalWo;
      }
      if (payload.billing_type !== 'RA Bill Basis') payload.ra_bill_frequency = null;

      let savedId;
      if (editingId) {
        const updated = await apiPut(`/projects/${editingId}`, payload);
        savedId = updated?.id || editingId;
        toast.success('Project updated');
      } else {
        const created = await apiPost('/projects', payload);
        savedId = created.id;
        // Upload BOQ if provided (create flow only)
        if (boqFile) {
          const fd = new FormData();
          fd.append('file', boqFile);
          try {
            const r = await api.post(`/boq/import-excel?project_id=${created.id}&replace=true`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(`Project created · ${r.data.imported} BOQ items imported (${r.data.sections.length} sections)`);
          } catch {
            toast.error('Project created but BOQ import failed — re-upload from BOQ tab');
          }
        } else {
          toast.success('Project created');
        }
      }
      setOpen(false);
      setEditingId(null);
      setForm({ ...EMPTY_FORM, payment_terms: { ...defaultPT } });
      setBoqFile(null);
      if (editingId) {
        await load();
      } else if (savedId) {
        navigate(`/app/projects/${savedId}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editingId ? 'Update failed' : 'Create failed'));
    }
    setSubmitting(false);
  };

  // Treat anything other than "Active" as inactive (Hold / Completed
  // / Cancelled / Archived / Pending). Inactive projects are hidden by default;
  // visible only to MD/CEO/CMD/CFO/super_admin via the "Show Inactive" toggle.
  const ACTIVE_STATUSES = new Set(['active']);
  const isActive = (p) => ACTIVE_STATUSES.has((p?.status || 'Active').toLowerCase());
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const allStatuses = useMemo(() => Array.from(new Set(projects.map(p => p.status).filter(Boolean))).sort(), [projects]);
  const allClients = useMemo(() => Array.from(new Set(projects.map(p => p.client).filter(Boolean))).sort(), [projects]);
  const visibleProjects = useMemo(() => {
    let list = isExec ? (showInactive ? projects : projects.filter(isActive)) : projects.filter(isActive);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q) ||
        (p.client || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) list = list.filter(p => (p.status || '') === statusFilter);
    if (clientFilter) list = list.filter(p => (p.client || '') === clientFilter);
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, isExec, showInactive, query, statusFilter, clientFilter]);
  const inactiveCount = useMemo(() => projects.filter((p) => !isActive(p)).length, [projects]);

  return (
    <div>
      {MajorDeleteHost}
      <PageHeader
        overline="Project Management"
        title="Project Portfolio"
        subtitle={`${visibleProjects.length} ${showInactive || !isExec ? 'projects' : 'active projects'} across India · ${inrCompact(visibleProjects.reduce((s,p)=>s+(p.contract_value||0),0))} contract value`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {isExec && inactiveCount > 0 && (
              <button
                type="button"
                onClick={() => setShowInactive((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${showInactive ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-slate-300 text-slate-700 hover:border-[#3b4872]'}`}
                title={`${inactiveCount} inactive project${inactiveCount === 1 ? '' : 's'} hidden by default · visible only to MD/CEO/CMD/CFO/Super Admin`}
                data-testid="projects-toggle-inactive"
              >
                {showInactive ? <Eye size={13} /> : <EyeOff size={13} />}
                {showInactive ? `Hide Inactive (${inactiveCount})` : `Show Inactive (${inactiveCount})`}
                <Lock size={10} className="opacity-50" />
              </button>
            )}
            <button data-testid="projects-new-btn" onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] hover:-translate-y-px transition-all">
              <Plus size={15} /> New Project
            </button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="projects-filter-bar">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects · name, code, client, location…"
            data-testid="projects-search"
            className="w-full pl-8 pr-8 py-2 text-sm border border-slate-300 rounded-sm focus:border-[#3b4872] focus:outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={12} />
            </button>
          )}
        </div>
        <select
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="projects-filter-status"
          className="px-3 py-2 text-sm border border-slate-300 rounded-sm bg-white focus:border-[#3b4872] focus:outline-none"
        >
          <option value="">All Statuses</option>
          {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
          data-testid="projects-filter-client"
          className="px-3 py-2 text-sm border border-slate-300 rounded-sm bg-white focus:border-[#3b4872] focus:outline-none max-w-[200px]"
        >
          <option value="">All Clients</option>
          {allClients.map(c => <option key={c} value={c}>{c.length > 30 ? c.slice(0, 30) + '…' : c}</option>)}
        </select>
        {(query || statusFilter || clientFilter) && (
          <button
            type="button"
            onClick={() => { setQuery(''); setStatusFilter(''); setClientFilter(''); }}
            className="px-3 py-2 text-xs font-display font-bold uppercase tracking-wider text-slate-600 hover:text-rose-600"
            data-testid="projects-clear-filters"
          >
            Clear
          </button>
        )}
        {/* Iter 146 — Cards / List view toggle (matches EnquiryTracker pattern). */}
        <div className="flex items-center gap-1 ml-2 bg-slate-100 rounded-sm p-0.5">
          <button
            type="button"
            data-testid="projects-view-cards"
            onClick={() => setView('cards')}
            className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm ${view === 'cards' ? 'bg-[#3b4872] text-white hover:bg-[#2d3856]' : 'text-slate-600 hover:bg-slate-200'}`}
          >
            Cards
          </button>
          <button
            type="button"
            data-testid="projects-view-list"
            onClick={() => setView('list')}
            className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm ${view === 'list' ? 'bg-[#3b4872] text-white hover:bg-[#2d3856]' : 'text-slate-600 hover:bg-slate-200'}`}
          >
            List
          </button>
        </div>
        <span className="ml-auto text-[11px] text-slate-500">
          Showing <b>{visibleProjects.length}</b> of {projects.length}
        </span>
      </div>

      {view === 'cards' && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading && <div className="col-span-full text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading…</div>}
        {!loading && visibleProjects.length === 0 && (
          <div className="col-span-full text-center py-16 bg-white border border-slate-200 rounded-sm">
            <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No {showInactive || !isExec ? '' : 'active '}projects</div>
            <button onClick={openCreate} className="text-[#3b4872] font-display font-semibold text-sm">Create your first project</button>
          </div>
        )}
        {visibleProjects.map(p => (
          <Link
            key={p.id}
            to={`/app/projects/${p.id}`}
            data-testid={`project-card-${p.code}`}
            className="bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872]/50 hover:-translate-y-px transition-all group relative"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{p.code}</div>
                <h3 className="font-display font-extrabold text-lg mt-1 leading-tight tracking-tight">{p.name}</h3>
                <div className="text-xs text-slate-500 mt-1">{p.client}</div>
                {p.contract_type && (
                  <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-[10px] font-mono uppercase tracking-wider text-slate-600">
                    {p.contract_type}
                  </div>
                )}
              </div>
              <span className={`tag ${statusColor(p.status)}`}>{p.status}</span>
            </div>
            <button
              type="button"
              onClick={(e) => handleDelete(e, p)}
              className="absolute top-3 right-3 p-1.5 rounded-sm text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Request deletion"
              data-testid={`del-project-${p.code}`}
            >
              <Trash2 size={13} />
            </button>
            <button
              type="button"
              onClick={(e) => openEdit(e, p)}
              className="absolute top-3 right-9 p-1.5 rounded-sm text-slate-300 hover:text-[#3b4872] hover:bg-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit project"
              data-testid={`edit-project-${p.code}`}
            >
              <Pencil size={13} />
            </button>
            <div className="flex items-center gap-3 text-xs text-slate-600 mb-4 flex-wrap">
              <span className="flex items-center gap-1"><MapPin size={11} />{p.location || '—'}</span>
              <span className="flex items-center gap-1"><Calendar size={11} />{formatDate(p.end_date)}</span>
              {p.geofence?.lat && p.geofence?.lng && (
                <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm font-mono text-[10px]" title={`${p.geofence.lat.toFixed(4)}, ${p.geofence.lng.toFixed(4)} · ${p.geofence.radius_m || 500}m`}>
                  <MapPinned size={10} /> Geofenced
                </span>
              )}
              {Array.isArray(p.team) && p.team.length > 0 && (
                <span
                  className="flex items-center gap-1 text-[#3b4872] bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-sm font-mono text-[10px]"
                  title={p.team.slice(0, 6).map(m => `${m.name} (${m.role})`).join('\n') + (p.team.length > 6 ? `\n+${p.team.length - 6} more` : '')}
                >
                  <Users size={10} /> {p.team.length}-member team
                </span>
              )}
            </div>
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="kpi-overline">Progress</span>
                <span className="font-display font-bold tabular-nums" title={p.progress_basis ? `Basis: ${p.progress_basis}` : ''} data-testid={`project-progress-${p.id}`}>{p.progress == null ? '—' : `${p.progress}%`}</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${p.progress || 0}%`, background: p.progress >= 80 ? '#10B981' : p.progress >= 40 ? '#3b4872' : '#94A3B8' }} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
              <div>
                <div className="kpi-overline text-[9px]">Value</div>
                <div className="font-display font-bold text-xs mt-0.5">{inrCompact(p.contract_value)}</div>
              </div>
              <div>
                <div className="kpi-overline text-[9px]">Spent</div>
                <div className="font-display font-bold text-xs mt-0.5">{inrCompact(p.spent)}</div>
              </div>
              <div>
                <div className="kpi-overline text-[9px]">Risk</div>
                <div className="mt-0.5"><span className={`tag ${statusColor(p.risk_level)}`}>{p.risk_level}</span></div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
              <span>PM · <span className="text-[#0F172A] font-medium">{p.pm}</span></span>
              <ArrowRight size={13} className="group-hover:translate-x-1 group-hover:text-[#3b4872] transition-all" />
            </div>
          </Link>
        ))}
      </div>
      )}

      {view === 'list' && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="projects-list-view">
          {loading && (
            <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading…</div>
          )}
          {!loading && visibleProjects.length === 0 && (
            <div className="text-center py-16">
              <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No {showInactive || !isExec ? '' : 'active '}projects</div>
              <button onClick={openCreate} className="text-[#3b4872] font-display font-semibold text-sm">Create your first project</button>
            </div>
          )}
          {!loading && visibleProjects.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full dense-table" data-testid="projects-list-table">
                <thead>
                  <tr>
                    <th className="text-left">Code</th>
                    <th className="text-left">Project</th>
                    <th className="text-left">Client</th>
                    <th className="text-left">Status</th>
                    <th className="text-left">Project Manager</th>
                    <th className="text-left">Start</th>
                    <th className="text-right">Contract Value</th>
                    <th className="text-right">Progress</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleProjects.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/app/projects/${p.id}`)}
                      data-testid={`project-row-${p.code}`}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <td className="font-mono text-[11px] uppercase text-[#3b4872]">{p.code}</td>
                      <td>
                        <div className="font-display font-bold text-sm text-[#0F172A] leading-tight">{p.name}</div>
                        {p.location && <div className="text-[10px] text-slate-500 mt-0.5">{p.location}</div>}
                      </td>
                      <td className="text-xs text-slate-600">{p.client || '—'}</td>
                      <td><span className={`tag ${statusColor(p.status)}`}>{p.status}</span></td>
                      <td className="text-xs">{p.pm || '—'}</td>
                      <td className="text-xs whitespace-nowrap">{formatDate(p.start_date) || '—'}</td>
                      <td className="text-xs text-right font-display font-bold tabular-nums">{inrCompact(p.contract_value)}</td>
                      <td className="text-right">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                            <div
                              className="h-full"
                              style={{
                                width: `${p.progress || 0}%`,
                                background: (p.progress || 0) >= 80 ? '#10B981' : (p.progress || 0) >= 40 ? '#3b4872' : '#94A3B8',
                              }}
                            />
                          </div>
                          <span className="text-[11px] font-display font-bold tabular-nums w-8 text-right" title={p.progress_basis ? `Basis: ${p.progress_basis}` : ''}>{p.progress == null ? '—' : `${p.progress}%`}</span>
                        </div>
                      </td>
                      <td className="text-right">
                        <ArrowRight size={13} className="text-slate-400 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl" onClick={e=>e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{editingId ? 'Edit' : 'New'}</div>
              <h3 className="font-display font-bold text-xl mt-1" data-testid="project-modal-title">{editingId ? 'Edit Project' : 'Create Project'}</h3>
            </div>
            <form onSubmit={submit} className="p-5 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
              {[
                { k:'code', cfg:'code', l:'Project Code', defaultReq: true },
                { k:'wo_code', l:'First Work Order Code', defaultReq: !editingId, hideWhenEditing: true, autoFill: 'wo' },
                // Client moved ABOVE Project Name and switched to
                // searchable+addable combobox sourced from CRM Companies.
                { k:'client', cfg:'client_name', l:'Client / Company', full:true, defaultReq: true, kind: 'client' },
                { k:'name', cfg:'name', l:'Project Name', full:true, defaultReq: true },
                { k:'type', l:'Type' },
                { k:'location', cfg:'location', l:'Location' },
                { k:'pm', l:'Project Manager', kind: 'employee' },
                { k:'start_date', cfg:'start_date', l:'Start Date', t:'date', defaultReq: true },
                { k:'end_date', cfg:'end_date', l:'End Date', t:'date', defaultReq: true },
                { k:'contract_value', cfg:'contract_value', l:'Contract Value (₹)', t:'number', defaultReq: true },
              ].filter(f => !(f.cfg && fc.isHidden(f.cfg)) && !(f.hideWhenEditing && editingId)).map(f => {
                const required = f.cfg ? fc.isRequired(f.cfg, f.defaultReq) : !!f.defaultReq;
                const labelText = f.cfg ? fc.label(f.cfg, f.l) : f.l;
                const helpText = f.cfg ? fc.help(f.cfg) :
                  (f.autoFill === 'wo' ? `Auto-suggested as <code>${(form.code || 'CODE').toUpperCase()}.1</code>. Edit if you have a custom client WO reference.` : null);
                // For the WO field, auto-fill from project code when blank
                let value = form[f.k] || '';
                if (f.autoFill === 'wo' && !value && form.code) value = `${form.code.toUpperCase()}.1`;
                return (
                <div key={f.k} className={f.full ? 'col-span-2' : ''}>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                    {labelText}{required && <span className="text-rose-500 ml-0.5">*</span>}
                  </label>
                  {f.kind === 'client' ? (
                    <ClientCombobox
                      value={value}
                      onChange={(v) => setForm({ ...form, client: v })}
                      required={required}
                      testid={`project-form-${f.k}`}
                    />
                  ) : f.kind === 'employee' ? (
                    <EmployeePicker
                      value={value}
                      onChange={(name, empId, empObj) => setForm({ ...form, pm: name, pm_employee_uuid: empId || null, pm_email: empObj?.email || null, pm_emp_id: empObj?.emp_id || null })}
                      placeholder="Search Project Manager by name or emp ID…"
                      allowFreeText
                      testid={`project-form-${f.k}`}
                    />
                  ) : f.k === 'location' ? (
                    <LocationCombobox
                      value={value}
                      onChange={(v) => setForm({ ...form, location: v })}
                      required={required}
                      testid={`project-form-${f.k}`}
                    />
                  ) : (
                    <input type={f.t || 'text'} min={f.t === 'number' ? '0' : undefined} value={value} onChange={e=>setForm({...form,[f.k]:e.target.value})}
                      required={required}
                      data-testid={`project-form-${f.k}`}
                      className={`w-full px-3 py-2 border rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none ${formErrors[f.k] ? 'border-rose-400 ring-1 ring-rose-300' : 'border-slate-300'}`} />
                  )}
                  {formErrors[f.k] && <div className="text-[10px] text-rose-600 mt-0.5" data-testid={`project-error-${f.k}`}>{formErrors[f.k]}</div>}
                  {helpText && <div className="text-[10px] text-slate-500 mt-0.5" dangerouslySetInnerHTML={{ __html: helpText }} />}
                </div>
              );
              })}

              <SiteDeliveryFields form={form} setForm={setForm} errors={formErrors} />

              {/* Geofence — site coordinates for mobile attendance */}
              <div className="col-span-2 bg-emerald-50/40 border border-emerald-200 rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-emerald-800">Site Geofence (Optional)</div>
                    <p className="text-[11px] text-slate-600 mt-0.5">Set the site's GPS coordinates so on-site mobile attendance can be validated.</p>
                  </div>
                  <MapPinned size={16} className="text-emerald-700" />
                </div>

                {/* Use my current location — fastest path for field engineers standing at the site */}
                <button
                  type="button"
                  onClick={useMyLocation}
                  disabled={resolvingLoc}
                  className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 bg-white border border-emerald-300 text-emerald-800 text-xs font-display font-bold uppercase tracking-wider rounded-sm hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="project-form-use-my-location"
                >
                  <Crosshair size={13} /> {resolvingLoc ? 'Getting location…' : 'Use my current location'}
                </button>

                <div className="text-[9px] text-center uppercase tracking-widest text-slate-400 mb-2">— OR —</div>

                <div className="grid grid-cols-12 gap-2 mb-1">
                  <input
                    type="text"
                    value={form.maps_link || ''}
                    onChange={e => setForm({...form, maps_link: e.target.value})}
                    placeholder='Paste Google Maps link (https://maps.app.goo.gl/...) or "lat,lng"'
                    className="col-span-9 px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
                    data-testid="project-form-maps-link"
                  />
                  <button
                    type="button"
                    onClick={resolveMapsLink}
                    disabled={resolvingLoc || !form.maps_link}
                    className="col-span-3 px-3 py-2 bg-emerald-600 text-white text-xs font-display font-bold uppercase tracking-wider rounded-sm hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="project-form-resolve-link"
                  >
                    {resolvingLoc ? 'Resolving…' : 'Use Link'}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mb-2 leading-snug">
                  Tip: open Google Maps → tap the <strong>business pin</strong> → <strong>Share → Copy link</strong> (gives a <code className="px-1 bg-slate-100 rounded">maps.app.goo.gl/…</code> URL). Plain search URLs without a selected pin don't carry coordinates.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Latitude</label>
                    <input type="number" step="any" value={form.geofence?.lat ?? ''}
                      onChange={e=>setForm({...form, geofence: {...form.geofence, lat: e.target.value}})}
                      placeholder="e.g. 18.5204"
                      data-testid="project-form-geo-lat"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none font-mono" />
                  </div>
                  <div>
                    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Longitude</label>
                    <input type="number" step="any" value={form.geofence?.lng ?? ''}
                      onChange={e=>setForm({...form, geofence: {...form.geofence, lng: e.target.value}})}
                      placeholder="e.g. 73.8567"
                      data-testid="project-form-geo-lng"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none font-mono" />
                  </div>
                  <div>
                    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Radius (m)</label>
                    <input type="number" min={10} max={10000} value={form.geofence?.radius_m ?? 500}
                      onChange={e=>setForm({...form, geofence: {...form.geofence, radius_m: e.target.value}})}
                      data-testid="project-form-geo-radius"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none font-mono" />
                    {Number(form.geofence?.radius_m) > 0 && Number(form.geofence?.radius_m) < 300 && (
                      <p className="mt-1.5 text-[11px] leading-snug text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="geofence-radius-warning">
                        For outdoor construction sites, we recommend a minimum radius of 500m. Mobile GPS accuracy outdoors is typically ±50–200m but can reach ±2000m under heavy cloud or weak signal.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Team — multi-member picker with roles */}
              <div className="col-span-2 bg-indigo-50/40 border border-indigo-200 rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872]">Project Team (Optional)</div>
                    <p className="text-[11px] text-slate-600 mt-0.5">Pick the on-site team. Each member gets a role tag. The PM field above is the lead.</p>
                  </div>
                  <span className="text-[10px] font-mono text-[#3b4872] bg-white border border-indigo-200 rounded px-2 py-0.5">
                    {(form.team || []).length} member{(form.team || []).length === 1 ? '' : 's'}
                  </span>
                </div>
                <ProjectTeamPicker
                  value={form.team || []}
                  onChange={(team) => setForm({ ...form, team })}
                />
              </div>

              <div className="col-span-2">
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Project Type</label>
                <div className="grid grid-cols-3 gap-2" data-testid="project-form-project-type">
                  {['Site', 'Office', 'Other'].map(pt => (
                    <button
                      key={pt}
                      type="button"
                      data-testid={`project-type-${pt.toLowerCase()}`}
                      onClick={() => setForm({ ...form, project_type: pt })}
                      className={`px-3 py-2 text-xs font-display font-semibold border rounded-sm transition-all ${ (form.project_type || 'Site') === pt ? 'border-[#3b4872] bg-indigo-50 text-[#3b4872]' : 'border-slate-300 hover:border-slate-400 text-slate-600'}`}
                    >
                      {pt}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  <strong>Site</strong> — appears in project dropdowns across the ERP. <strong>Office</strong> / <strong>Other</strong> — used as cost-centres for company-wide overhead expenses (Petty Cash, SaaS credits, office events).
                </div>
              </div>

              <div className="col-span-2">
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Contract Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.keys(templates).map(ct => (
                    <button key={ct} type="button" data-testid={`contract-type-${ct.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
                      onClick={() => setForm({...form, contract_type: ct})}
                      className={`px-3 py-2 text-xs font-display font-semibold border rounded-sm transition-all ${form.contract_type === ct ? 'border-[#3b4872] bg-indigo-50 text-[#3b4872]' : 'border-slate-300 hover:border-slate-400 text-slate-600'}`}>
                      {ct}
                    </button>
                  ))}
                </div>
              </div>

              {form.contract_type && templates[form.contract_type] && (
                <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-3">
                  <div className="kpi-overline mb-2">Auto-generated stages for {form.contract_type}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {templates[form.contract_type].map((s, i) => (
                      <span key={i} className="px-2 py-1 bg-white border border-slate-200 rounded-sm text-[11px] font-mono">
                        <span className="text-slate-400 mr-1">{i+1}.</span>{s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <BillingPaymentTermsFields
                billingType={form.billing_type}
                raBillFrequency={form.ra_bill_frequency}
                paymentTerms={form.payment_terms}
                billingTypes={billingTypes}
                raFrequencies={raFrequencies}
                testIdPrefix="create"
                onChange={({ billingType, raBillFrequency, paymentTerms }) =>
                  setForm({
                    ...form,
                    billing_type: billingType,
                    ra_bill_frequency: raBillFrequency,
                    payment_terms: paymentTerms,
                  })
                }
              />

              {!editingId && (
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Upload BOQ Excel (optional)</label>
                    <button type="button" data-testid="create-boq-sample-link" onClick={async () => {
                      try {
                        const r = await api.get('/boq-sample-template', { responseType: 'blob' });
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement('a');
                        a.href = url; a.download = 'Ellora_BOQ_Sample_Template.xlsx';
                        document.body.appendChild(a); a.click(); a.remove();
                        URL.revokeObjectURL(url);
                      } catch { toast.error('Download failed'); }
                    }}
                      className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872] hover:text-[#2c3658]">
                      ↓ Sample Template
                    </button>
                  </div>
                  <label htmlFor="boq-file-input" data-testid="boq-file-picker" className="flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-slate-300 rounded-sm hover:border-[#3b4872] hover:bg-indigo-50/30 cursor-pointer transition-colors">
                    <FileSpreadsheet size={16} className={boqFile ? 'text-[#3b4872]' : 'text-slate-400'} />
                    <span className={`text-xs font-display font-semibold ${boqFile ? 'text-[#3b4872]' : 'text-slate-500'}`}>
                      {boqFile ? boqFile.name : 'Drop or click to attach client BOQ (.xlsx) — auto-imported on create'}
                    </span>
                    <input id="boq-file-input" type="file" accept=".xlsx,.xls,.xlsm" onChange={e => setBoqFile(e.target.files?.[0] || null)} className="hidden" />
                  </label>
                </div>
              )}

              {editingId && (
                <div className="col-span-2 pt-3 border-t border-slate-200">
                  <DriveAttachments
                    contextType="project"
                    contextId={editingId}
                    category="Project Documents"
                    title="Project Documents · Google Drive"
                  />
                </div>
              )}

              <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button type="button" onClick={()=>{ setOpen(false); setEditingId(null); }} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
                <button data-testid="projects-save-btn" type="submit" disabled={submitting} className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
                  {submitting ? (editingId ? 'Saving…' : 'Creating…') : (editingId ? 'Save Changes' : (boqFile ? 'Create + Import BOQ' : 'Create'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
